import ApiClient, { ApiResponse } from "./api-client";
import { hostURL } from "utils/helper/states";

/**
 * Typed calls for the customer features shared with the mobile app
 * (see WEB_PARITY.md). Every function returns the API envelope unchanged.
 */

const api = (path: string) => `${hostURL}/api/${path}`;

/** Builds a query string, leaving out empty values. */
export function queryString(params: Record<string, string | number | undefined | null>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    query.append(key, String(value));
  }
  const text = query.toString();
  return text ? `?${text}` : "";
}

// ---------------------------------------------------------------- recovery
// Step 1 returns a short-lived recovery token; steps 2 and 3 send it as the
// Authorization header. It is not a login session.

export const requestPasswordReset = (email: string) =>
  ApiClient.post({ url: api("auth/user/forgot-password"), body: { email: email.trim() } });

export const verifyPasswordResetCode = (recoveryToken: string, verificationCode: string) =>
  ApiClient.post({
    url: api("auth/user/forgot-password-verification"),
    token: recoveryToken,
    body: { verificationCode },
  });

export const resetForgottenPassword = (recoveryToken: string, newPassword: string) =>
  ApiClient.post({
    url: api("auth/user/forgot-password-reset"),
    token: recoveryToken,
    body: { newPassword },
  });

// ------------------------------------------------------------------ account

export const resendEmailVerification = (email: string, token: string, isProvider = false) =>
  ApiClient.get(
    api(`auth/${isProvider ? "sp" : "user"}/resend_email_verification/${encodeURIComponent(email)}`),
    token
  );

export const trackLogin = (token: string, isProvider = false) =>
  ApiClient.get(api(`auth/${isProvider ? "sp" : "user"}/track-logins`), token);

export const fetchUserDetails = (token: string) =>
  ApiClient.post({ url: api("auth/user/details"), token, body: {} });

/**
 * Revokes the server session. Uses fetch directly so a 401 (session already
 * gone) does not trigger the session-expired handler a second time.
 */
export async function revokeSession(token: string, isProvider = false) {
  try {
    await fetch(api(`auth/${isProvider ? "sp" : "user"}/logout`), {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: token },
      body: "{}",
    });
  } catch {
    // Best effort: local sign-out continues regardless.
  }
}

// ---------------------------------------------------------------- providers

export type ProviderSearch = {
  service?: string | null;
  search?: string;
  lat?: number;
  lng?: number;
};

export const providerSearchUrl = (params: ProviderSearch) =>
  api("services/get-service-providers") + queryString(params);

export const fetchUserRating = (serviceId: string, token: string) =>
  ApiClient.get(api(`services/get-user-rating/${serviceId}`), token);

export const updateUserRating = (
  body: { serviceId: string; rate: number; comment: string; showName: boolean },
  token: string
) => ApiClient.post({ url: api("services/update-user-rating"), token, body });

export const reportProvider = (
  body: { serviceId: string; houseId?: string; title: string; reason: string },
  token: string
) => ApiClient.post({ url: api("services/report-service-provider"), token, body });

// ------------------------------------------------------------------- houses

/** `nearby` filters to the region; without it matching houses rank first. */
export type HouseSearch = {
  search?: string;
  type?: string;
  country?: string;
  state?: string;
  nearby?: boolean;
};

export const houseSearchUrl = ({ nearby, ...params }: HouseSearch) =>
  api("agents/get-houses") + queryString({ ...params, nearby: nearby ? 1 : undefined });

export type HouseType = { _id: string; name: string };

export const fetchHouseTypes = (token: string) => ApiClient.get(api("agents/get-house-types"), token);

// ------------------------------------------------------------ notifications

export const markNotificationSeen = (notificationId: string, token: string) =>
  ApiClient.post({ url: api("notifications/seen-notification"), token, body: { notificationId } });

export const deleteNotification = (notificationId: string, token: string) =>
  ApiClient.delete({ url: api("notifications/delete-notification"), token, body: { notificationId } });

// ------------------------------------------------------------- job requests
// Customers post requests (/api/user/request); providers whose services
// match respond to them (/api/sp/request). See WEB_PARITY.md.

export type JobRequest = {
  _id: string;
  userId: string;
  title: string;
  description: string;
  service: string;
  minBudget: string;
  maxBudget?: string | null;
  address?: string | null;
  city: string;
  closed: boolean;
  deleted?: boolean;
  agentCount: number;
  approvedAgentId?: string | null;
  approvedResponseId?: string | null;
  createdAt: string;
  updatedAt?: string;
  /** Provider list only: this provider's own response, if any. */
  agentData?: { _id: string; agentAccepted: boolean | null; agentNegotiated: boolean };
};

export type JobResponse = {
  _id: string;
  jobRequestId: string;
  agentId: string;
  reason?: string | null;
  minBudget?: string | null;
  maxBudget?: string | null;
  userAccepted?: boolean | null;
  agentAccepted?: boolean | null;
  agentNegotiated: boolean;
  createdAt: string;
  /** Customer view only; absent when the provider no longer exists. */
  agentData?: {
    _id: string;
    profileImage?: string | null;
    businessName?: string | null;
    businessVerified?: boolean | null;
    ratingAverage?: number | null;
  };
};

export type JobRequestInput = {
  title: string;
  description: string;
  service: string;
  address: string;
  city: string;
  minBudget: string;
  maxBudget?: string;
  /** GeoJSON order: [longitude, latitude]. */
  coordinates: [number, number];
};

export const createJobRequest = (body: JobRequestInput, token: string) =>
  ApiClient.post({ url: api("user/request/create-job-request"), token, body });

export const fetchMyJobRequests = (token: string, page = 1) =>
  ApiClient.get(api("user/request/get-job-request") + queryString({ page, limit: 20 }), token);

export const fetchJobResponses = (requestId: string, token: string) =>
  ApiClient.get(api(`user/request/get-job-request-details/${requestId}`), token);

export const decideOnJobResponse = (responseId: string, accept: boolean, token: string) =>
  ApiClient.post({ url: api("user/request/approve-job-request"), token, body: { id: responseId, accept } });

export const updateJobRequest = (
  id: string,
  changes: Partial<Omit<JobRequestInput, "service">> | { delete: true },
  token: string
) => ApiClient.post({ url: api("user/request/update-job-request"), token, body: { id, ...changes } });

export const fetchProviderJobRequests = (token: string) =>
  ApiClient.get(api("sp/request/get-job-request"), token);

export const fetchMyJobResponse = (requestId: string, token: string) =>
  ApiClient.get(api(`sp/request/get-job-request-details/${requestId}`), token);

/**
 * Accept or decline at the customer's budget, or quote a different one; the
 * API decides whether it counts as a negotiation (quote above the maximum).
 */
export const respondToJobRequest = (
  body: { jobRequestId: string; accept: boolean; minBudget?: string; maxBudget?: string; reason?: string },
  token: string
) => ApiClient.post({ url: api("sp/request/accept-job-request"), token, body });

/** Removes thousands separators; the API stores budgets as plain numbers. */
export const cleanBudget = (value: string) => value.replace(/[,\s]/g, "");

// --------------------------------------------------------------------- chat

export const sendVoiceNote = (
  token: string,
  audio: File,
  fields: { chatId: string; date: string; syncId: string; reply?: string; replyId?: string }
): Promise<ApiResponse> =>
  ApiClient.postAudio({ url: api("chat/audio-upload"), token, audio, fields });
